import _ from 'underscore'
import { sortXhprofGenericDataObject, XhprofCall } from './XhprofProfiler'
import { parseParentChild, XhprofCallEdge, XhprofCallInfo, XhprofFlatInfoData, XhprofRawData } from './XhprofReportGenerator'

export interface XhprofCallgraph {
    total: number
    totalMemory: number
    nodes: XhprofCallgraphNode[]
    edges: XhprofCallgraphEdge[]
    /* @deprecated no longer needed, when unified callgraph ui becomes default */
    calls?: XhprofFlatInfoData
}

export interface XhprofCallgraphNode {
    id: string
    name: string
    type: string
    foldedNodes: string[]
}

export interface XhprofNode extends XhprofCallgraphNode {
    id: string
    name: string
    type: 'call'
    namespace?: string
    ct: number
    excl_wt: number
    pmu: number
    excl_pmu: number
    fqcn?: string
    wt: number
    foldedNodes: string[]
}

export interface XhprofFoldedNode extends XhprofCallgraphNode {
    id: string
    name: string
    type: 'folded'
    foldedNodes: string[]
}

export interface XhprofCallgraphEdge {
    ct: number
    source: string
    target: string
    wt: number
    pmu: number
}

export const encodeXhprofCallgraphNodeId = (callString: string): string =>
    window
        .btoa(callString)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/[=]+$/g, '')

export const filterXhprofCallInfoAroundSelectedFunction = (
    data: XhprofFlatInfoData,
    functionName: string,
    numberOfAdjacents: number,
): XhprofFlatInfoData => {
    if (!data[functionName]) {
        return {}
    }
    const adjacents = { ...data[functionName].parents, ...data[functionName].children }

    const requiredWallTime = Object.keys(adjacents)
        .map(adjacent => adjacents[adjacent])
        .sort((wallTimeA, wallTimeB) => wallTimeB.wt - wallTimeA.wt)
        .slice(0, numberOfAdjacents)
        .pop()

    if (!requiredWallTime) {
        return {}
    }

    const parents = _.pick(data[functionName].parents, (wallTime, parent) => wallTime >= requiredWallTime)
    const children = _.pick(data[functionName].children, (wallTime, child) => wallTime >= requiredWallTime)

    return _.pick(data, (callData, call) => call === functionName || call in parents || call in children) as XhprofFlatInfoData
}

const filterXhprofEdgesAroundSelectedFunction = (
    filteredDataInfo: XhprofFlatInfoData,
    rawData: XhprofRawData,
): { edges: XhprofCallgraphEdge[]; nodes: XhprofCallgraphNode[] } => {
    const edges: XhprofCallgraphEdge[] = []
    let nodes: XhprofNode[] = []

    Object.keys(rawData).forEach((parentChild: string) => {
        const [parent, child] = parseParentChild(parentChild)

        if (!parent && child in filteredDataInfo) {
            nodes = addNode(nodes, child, filteredDataInfo[child])
            return
        }

        if (!parent) {
            return
        }

        if (!(parent in filteredDataInfo) || !(child in filteredDataInfo)) {
            return
        }

        nodes = addNode(nodes, child, filteredDataInfo[child])
        nodes = addNode(nodes, parent, filteredDataInfo[parent])

        const info = rawData[parentChild]

        edges.push({
            source: encodeXhprofCallgraphNodeId(parent),
            target: encodeXhprofCallgraphNodeId(child),
            wt: info.wt,
            ct: info.ct,
            pmu: info.pmu || 0,
        })
    })

    return { edges, nodes }
}

const addNode = (nodes: XhprofNode[], functionName: string, callInfo: XhprofCallInfo): XhprofNode[] => {
    if (nodes.filter(existingNode => existingNode.fqcn === functionName).length !== 0) {
        return nodes
    }

    const call = new XhprofCall(functionName)
    const node: XhprofNode = {
        id: encodeXhprofCallgraphNodeId(functionName),
        name: call.shortCallName,
        type: 'call',
        namespace: call.namespaceName,
        fqcn: functionName,
        ct: callInfo.ct,
        wt: callInfo.wt,
        excl_wt: callInfo.excl_wt,
        pmu: callInfo.pmu || 0,
        excl_pmu: callInfo.excl_pmu || 0,
        foldedNodes: callInfo.foldedNodes || [],
    }

    return [...nodes, node]
}

export const convertToDotXhporfCallgraph = (
    functionName: string,
    rawData: XhprofRawData,
    numberOfAdjacents: number = 5,
    data: XhprofFlatInfoData,
): XhprofCallgraph => {
    let total = 0
    let totalMemory = 0

    const filteredData: XhprofFlatInfoData = filterXhprofCallInfoAroundSelectedFunction(data, functionName, numberOfAdjacents)

    if (filteredData[functionName]) {
        total = filteredData[functionName]?.wt || 0
        totalMemory = filteredData[functionName]?.pmu || 0
    }

    const { edges, nodes } = filterXhprofEdgesAroundSelectedFunction(filteredData, rawData)

    return { nodes, edges, total, totalMemory }
}

const XhprofCallgraphConfiguration = {
    max_perc_wt: 5,
    max_start_count: 3,
    max_parents: 1,
    max_nodes: 100,
}
export const fullXhprofCallgraph = (
    data: XhprofFlatInfoData,
    diffTrace?: boolean,
    mandatoryCalls: string[] = [],
    memory: boolean = false,
): XhprofCallgraph => {
    const config = XhprofCallgraphConfiguration

    const field = (callData: XhprofCallInfo): number => (memory ? Math.abs(callData.pmu || 0) : Math.abs(callData.wt))
    const exclField = (callData: XhprofCallInfo): number => (memory ? Math.abs(callData.excl_pmu || 0) : Math.abs(callData.excl_wt))

    const main = data['main()']
    let hotpathCallgraph: XhprofCallgraph = { total: main.wt, totalMemory: main.pmu || 0, nodes: [], edges: [], calls: {} }

    const topCalls = Object.keys(data)
        .map(call => ({ call, ...data[call] }))
        .filter(callData => exclField(callData) >= field(callData) * (config.max_perc_wt / 100))
        .sort((a, b) => field(b) - field(a))
        .map(callData => callData.call)
        .slice(0, config.max_start_count)

    const leafs = [...topCalls, ...mandatoryCalls]

    leafs
        .filter(call => call in data)
        .forEach(call => {
            hotpathCallgraph = reserveXhprofTree(hotpathCallgraph, call, data[call].parents, data, memory, diffTrace)
        })

    if (hotpathCallgraph.calls) {
        hotpathCallgraph.calls = sortXhprofGenericDataObject(hotpathCallgraph.calls, memory ? 'excl_pmu' : 'excl_wt')
    }

    hotpathCallgraph = addFoldedNodes(hotpathCallgraph)

    return hotpathCallgraph
}

const reserveXhprofTree = (
    hotpathCallgraph: XhprofCallgraph,
    startFunction: string,
    parents: { [parent: string]: XhprofCallEdge },
    sortedData: XhprofFlatInfoData,
    memory: boolean,
    diffTrace?: boolean,
): XhprofCallgraph => {
    const config = XhprofCallgraphConfiguration

    if (!parents || config.max_nodes <= hotpathCallgraph.nodes.length) {
        return hotpathCallgraph
    }

    const maxParents = Object.keys(parents)
        .map(parent => parents[parent])
        .sort((parentA, parentB) => (memory ? Math.abs(parentB.pmu) - Math.abs(parentA.pmu) : Math.abs(parentB.wt) - Math.abs(parentA.wt)))
        .slice(0, config.max_parents)

    hotpathCallgraph.nodes = addNode(hotpathCallgraph.nodes as XhprofNode[], startFunction, sortedData[startFunction])
    if (hotpathCallgraph.calls) {
        hotpathCallgraph.calls[startFunction] = sortedData[startFunction]
        hotpathCallgraph.calls[startFunction].perc_wt = (hotpathCallgraph.calls[startFunction].wt / hotpathCallgraph.total) * 100
        hotpathCallgraph.calls[startFunction].perc_excl_wt = (hotpathCallgraph.calls[startFunction].excl_wt / hotpathCallgraph.total) * 100
    }

    maxParents.forEach(edgeInfo => {
        const parentValueData = sortedData[edgeInfo.name]
        const nextNodes = addNode(hotpathCallgraph.nodes as XhprofNode[], edgeInfo.name, parentValueData)
        hotpathCallgraph.edges.push({
            source: encodeXhprofCallgraphNodeId(edgeInfo.name),
            target: encodeXhprofCallgraphNodeId(startFunction),
            wt: parents[edgeInfo.name].wt,
            ct: parents[edgeInfo.name].ct,
            pmu: parents[edgeInfo.name].pmu,
        })
        if (nextNodes.length === hotpathCallgraph.nodes.length) {
            return hotpathCallgraph
        }
        hotpathCallgraph.nodes = nextNodes
        reserveXhprofTree(hotpathCallgraph, edgeInfo.name, parentValueData.parents, sortedData, memory, diffTrace)
    })

    return hotpathCallgraph
}

const addFoldedNodes = (callgraph: XhprofCallgraph): XhprofCallgraph => {
    callgraph.nodes.forEach(node => {
        if (node.foldedNodes.length > 0 && node.type === 'call') {
            const foldedNode: XhprofCallgraphNode = {
                id: 'fold' + node.id,
                name: 'folded nodes after ' + node.name,
                type: 'folded',
                foldedNodes: node.foldedNodes,
            }
            callgraph.nodes = [...callgraph.nodes, foldedNode]
            const previousEdges: XhprofCallgraphEdge[] = callgraph.edges.filter(edge => edge.source === node.id)
            previousEdges.forEach(edge => {
                const sourceEdge: XhprofCallgraphEdge = {
                    ct: edge.ct,
                    source: edge.source,
                    target: foldedNode.id,
                    wt: edge.wt,
                    pmu: edge.pmu,
                }
                const targetEdge: XhprofCallgraphEdge = {
                    ct: edge.ct,
                    wt: edge.wt,
                    pmu: edge.pmu,
                    source: foldedNode.id,
                    target: edge.target,
                }
                callgraph.edges = [...callgraph.edges.filter(e => e.source !== node.id), sourceEdge, targetEdge]
            })
            // if folded nodes were a leaf in tree
            if (previousEdges.length === 0) {
                const edgeAbove = callgraph.edges.filter(edge => edge.target === node.id)[0]
                const newEdge: XhprofCallgraphEdge = {
                    ct: edgeAbove.ct,
                    source: node.id,
                    target: foldedNode.id,
                    wt: edgeAbove.wt,
                    pmu: edgeAbove.pmu,
                }
                callgraph.edges = [...callgraph.edges, newEdge]
            }
        }
    })
    return callgraph
}
